import React from 'react';
import { Card, Row, Col, Statistic, Divider, Tooltip } from 'antd';
import { ThunderboltOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../utils/danishNumberFormatter';
import { COLORS } from '../../constants/theme';

// Danish labels for CAPEX items
const CAPEX_LABELS = {
  elkedel: 'Elkedel',
  tilslutning: 'Tilslutning til elnet',
  batterier: 'Batterier',
};

// Danish labels for OPEX items
const OPEX_LABELS = {
  elkøbArbitrage: 'Elkøb (Arbitrage)',
};

// Danish labels for Revenue items
const REVENUE_LABELS = {
  elsalgBatteri: 'Elsalg (Batteri)',
  systemydelserElkedel: 'Systemydelser (Elkedel - mFRR/aFRR)',
  systemydelserBatteri: 'Systemydelser (Batteri)',
  kapacitetsbetaling: 'Kapacitetsbetaling',
};

// Danish labels for Depreciation items with years
const DEPRECIATION_LABELS = {
  elkedel: 'Elkedel / 25 år',
  tilslutning: 'Tilslutning / 50 år',
  batterier: 'Batterier / 10 år',
};

/**
 * Electricity Company Financial Dashboard
 * Shows financial metrics specific to electricity company operations
 */
const ElectricityCompanyFinancials = ({ financialData }) => {
  if (!financialData || !financialData.electricity) {
    return null;
  }

  const { electricity } = financialData;

  return (
    <div>
      <h3 style={{ 
        color: COLORS.electricity, 
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <ThunderboltOutlined /> Elselskab
      </h3>

      <Row gutter={[16, 16]}>
        {/* Revenue Section */}
        <Col span={8}>
          <Card
            title={
              <span>
                Indtægter
                <Tooltip title="Elselskabets indtægter kommer fra elsalg fra batteri når prisen er høj (arbitrage), systemydelser fra elkedel (mFRR/aFRR) og batteri, samt kapacitetsbetaling for at stille kapacitet til rådighed for nettet.">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {Object.entries(electricity.revenue).map(([key, value]) => (
              value > 0 && (
                <div key={key} style={{ marginBottom: '0.5rem' }}>
                  <span style={{ 
                    fontSize: '0.875rem',
                    color: COLORS.ui.text.secondary,
                  }}>
                    {REVENUE_LABELS[key] || key}:
                  </span>
                  <span style={{ 
                    float: 'right',
                    fontWeight: 500,
                    color: COLORS.ui.text.primary,
                  }}>
                    {formatCurrency(value, 'DKK', 0)}
                  </span>
                </div>
              )
            ))}
            <Divider style={{ margin: '0.75rem 0' }} />
            <Statistic
              title="Total Indtægt"
              value={electricity.totalRevenue}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.financial.revenue, fontSize: '1.25rem' }}
            />
          </Card>
        </Col>

        {/* OPEX Section */}
        <Col span={8}>
          <Card
            title={
              <span>
                OPEX (Driftsomkostninger)
                <Tooltip title="Elselskabets driftsomkostninger er kun elkøb til arbitrage - når batteriet køber billig el (spot-pris) for at sælge den når prisen er højere. Dette er IKKE operational energikøb til varmeproduktion.">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {Object.entries(electricity.opex).map(([key, value]) => (
              value > 0 && (
                <div key={key} style={{ marginBottom: '0.5rem' }}>
                  <span style={{ 
                    fontSize: '0.875rem',
                    color: COLORS.ui.text.secondary,
                  }}>
                    {OPEX_LABELS[key] || key}:
                  </span>
                  <span style={{ 
                    float: 'right',
                    fontWeight: 500,
                    color: COLORS.ui.text.primary,
                  }}>
                    {formatCurrency(value, 'DKK', 0)}
                  </span>
                </div>
              )
            ))}
            <Divider style={{ margin: '0.75rem 0' }} />
            <Statistic
              title="Total OPEX"
              value={electricity.totalOpex}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.financial.opex, fontSize: '1.25rem' }}
            />
          </Card>
        </Col>

        {/* Depreciation Section */}
        <Col span={8}>
          <Card
            title={
              <span>
                Afskrivninger
                <Tooltip title="Årlige afskrivninger af elselskabets anlæg: Elkedel afskrives over 25 år (1.2M årligt fra 30M investering), og Batterier afskrives over 10 år (0.9M årligt fra 9M investering). Total årlig afskrivning i millioner DKK.">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {Object.entries(electricity.depreciation).map(([key, value]) => (
              value > 0 && (
                <div key={key} style={{ marginBottom: '0.5rem' }}>
                  <span style={{ 
                    fontSize: '0.875rem',
                    color: COLORS.ui.text.secondary,
                  }}>
                    {DEPRECIATION_LABELS[key] || key}:
                  </span>
                  <span style={{ 
                    float: 'right',
                    fontWeight: 500,
                    color: COLORS.ui.text.primary,
                  }}>
                    {formatCurrency(value * 1000000, 'DKK', 0)}
                  </span>
                </div>
              )
            ))}
            <Divider style={{ margin: '0.75rem 0' }} />
            <Statistic
              title="Total Afskrivning"
              value={electricity.totalDepreciation * 1000000}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.status.error, fontSize: '1.25rem' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
        {/* CAPEX Section */}
        <Col span={8}>
          <Card
            title={
              <span>
                CAPEX (Investeringer)
                <Tooltip title="Elselskabets investeringer består af Elkedel (30M DKK) til varmeproduktion og levering af systemydelser (mFRR/aFRR), samt Batterier (9M DKK) til el-arbitrage og systemydelser. Total investering: 39M DKK.">
                  <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
                </Tooltip>
              </span>
            }
            size="small"
            bordered={false}
            style={{ height: '100%' }}
          >
            {Object.entries(electricity.capex).map(([key, value]) => (
              value > 0 && (
                <div key={key} style={{ marginBottom: '0.5rem' }}>
                  <span style={{ 
                    fontSize: '0.875rem',
                    color: COLORS.ui.text.secondary,
                  }}>
                    {CAPEX_LABELS[key] || key}:
                  </span>
                  <span style={{ 
                    float: 'right',
                    fontWeight: 500,
                    color: COLORS.ui.text.primary,
                  }}>
                    {formatCurrency(value * 1000000, 'DKK', 0)}
                  </span>
                </div>
              )
            ))}
            <Divider style={{ margin: '0.75rem 0' }} />
            <Statistic
              title="Total CAPEX"
              value={electricity.totalCapex * 1000000}
              precision={0}
              suffix="DKK"
              valueStyle={{ color: COLORS.status.error, fontSize: '1.25rem' }}
            />
          </Card>
        </Col>
      </Row>

      {/* P&L Summary */}
      <Card
        title={
          <span>
            Resultatopgørelse (P&L)
            <Tooltip title="Elselskabets resultatopgørelse viser indtægter fra elsalg (batteri arbitrage), systemydelser (elkedel mFRR/aFRR + batteri) og kapacitetsbetaling, minus omkostninger til elkøb til arbitrage og afskrivninger af elkedel og batterier.">
              <InfoCircleOutlined style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: COLORS.ui.text.secondary }} />
            </Tooltip>
          </span>
        }
        style={{
          marginTop: '1rem',
          borderColor: electricity.netProfit >= 0 ? COLORS.status.success : COLORS.status.error,
        }}
        size="small"
      >
        <Row gutter={16}>
          <Col span={4}>
            <Tooltip title={`Elselskabets samlede indtægter fra elsalg batteri (${(electricity.revenue.elsalgBatteri / 1000000).toFixed(1)}M), systemydelser fra elkedel (${(electricity.revenue.systemydelserElkedel / 1000000).toFixed(1)}M) og batteri (${(electricity.revenue.systemydelserBatteri / 1000000).toFixed(1)}M), samt kapacitetsbetaling (${(electricity.revenue.kapacitetsbetaling / 1000000).toFixed(1)}M).`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>Indtægt <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={electricity.totalRevenue}
                  precision={0}
                  suffix="DKK"
                  prefix="+"
                  valueStyle={{ color: COLORS.status.success, fontWeight: 'bold' }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={4}>
            <Tooltip title={`Elselskabets driftsomkostninger er KUN elkøb til arbitrage (${(electricity.totalOpex / 1000000).toFixed(1)}M DKK) - når batteriet køber billig el for at sælge når prisen er højere. Dette er adskilt fra operational energikøb til varmeproduktion som tilhører varmeselskabet.`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>OPEX <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={electricity.totalOpex}
                  precision={0}
                  suffix="DKK"
                  prefix="−"
                  valueStyle={{ color: COLORS.status.error, fontWeight: 'bold' }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={4}>
            <Tooltip title={`EBITDA for elselskabet er driftsresultatet FØR afskrivninger. Beregnes som: Indtægt (${(electricity.totalRevenue / 1000000).toFixed(1)}M) minus OPEX (${(electricity.totalOpex / 1000000).toFixed(1)}M) = ${(electricity.ebitda / 1000000).toFixed(1)}M DKK. Viser rentabiliteten af el-arbitrage og systemydelser.`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>EBITDA <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={electricity.ebitda}
                  precision={0}
                  suffix="DKK"
                  prefix={electricity.ebitda >= 0 ? "+" : "−"}
                  valueStyle={{
                    color: electricity.ebitda >= 0 ? COLORS.status.success : COLORS.status.error,
                    fontWeight: 'bold',
                  }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={4}>
            <Tooltip title={`Elselskabets årlige afskrivninger: Elkedel (${(electricity.depreciation.elkedel || 0).toFixed(2)}M over 25 år fra 30M investering) og Batterier (${(electricity.depreciation.batterier || 0).toFixed(2)}M over 10 år fra 9M investering). Total: ${(electricity.totalDepreciation).toFixed(2)}M DKK årligt.`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>Afskrivning <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={electricity.totalDepreciation * 1000000}
                  precision={0}
                  suffix="DKK"
                  prefix="−"
                  valueStyle={{ color: COLORS.status.error, fontWeight: 'bold' }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={4}>
            <Tooltip title={`Elselskabets nettoresultat efter alle omkostninger. Beregnes som: EBITDA (${(electricity.ebitda / 1000000).toFixed(1)}M) minus Afskrivninger (${(electricity.totalDepreciation).toFixed(2)}M) = ${(electricity.netProfit / 1000000).toFixed(1)}M DKK. Dette overskud kan bruges til udbytter eller geninvesteringer.`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>Nettoresultat <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={Math.abs(electricity.netProfit)}
                  precision={0}
                  suffix="DKK"
                  prefix={electricity.netProfit >= 0 ? "+" : "−"}
                  valueStyle={{
                    color: electricity.netProfit >= 0 ? COLORS.status.success : COLORS.status.error,
                    fontWeight: 'bold',
                    fontSize: '1.125rem',
                  }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={4}>
            <Tooltip title={`Nettomargin viser hvor stor en andel af omsætningen der bliver til overskud. Beregnes som: Nettoresultat (${(electricity.netProfit / 1000000).toFixed(1)}M) / Indtægt (${(electricity.totalRevenue / 1000000).toFixed(1)}M) × 100 = ${(electricity.totalRevenue > 0 ? (electricity.netProfit / electricity.totalRevenue * 100) : 0).toFixed(1)}%.`}>
              <div style={{ cursor: 'help' }}>
                <Statistic
                  title={<span>Nettomargin <InfoCircleOutlined style={{ fontSize: '0.7rem' }} /></span>}
                  value={electricity.totalRevenue > 0 ? (electricity.netProfit / electricity.totalRevenue * 100) : 0}
                  precision={1}
                  suffix="%"
                  valueStyle={{
                    color: electricity.netProfit >= 0 ? COLORS.status.success : COLORS.status.error,
                  }}
                />
              </div>
            </Tooltip>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default ElectricityCompanyFinancials;
